import { useCallback, useEffect, useMemo, useState } from 'react';
import { InkBlock } from '@/components/atoms';
import { dwrClassesById } from '@/data/classes';
import { findMoveByIdGlobal } from '@/data/moves';
import type { MoveRef } from '@/schemas/character';
import type { ClassId } from '@/schemas/classId';
import { hasMoveSetup, type Move } from '@/schemas/move';
import { MoveCard } from '@/components/molecules/MoveCard';
import { MoveSetupPanel } from './MoveSetupPanel';
import { isMoveSetupComplete } from '../services/moveSetupComplete';
import { updateMoveChoiceGroup, updateMoveNarrativeField } from '../services/moveRefMutations';

/**
 * **FI-81 #1 (2026-05-24) + FI-86 (2026-05-25)** — Sezione del builder L1
 * per la **visualizzazione** delle mosse cross-class concesse
 * implicitamente da una mossa starting con `grantsMoveChoice from-choice-mapping`,
 * e per il **setup** di quelle che hanno `hasMoveSetup === true`.
 *
 * Casi pilota:
 *  - Paladino L1 → `paladino.ordine-sovrannaturale` + choice
 *    `vincolo-della-lama` → concede `warlock.vincolo-della-lama`
 *    (cross-class, con setup tipologia/portata/miglioramenti/descrizione).
 *  - Stesso anchor + choice `vincolo-della-catena` → concede
 *    `warlock.vincolo-della-catena` (cross-class, description-only).
 *
 * **FI-86**: la sezione mostra ora **tutte** le mosse cross-class
 * derivate come `<MoveCard>` espansa (description completa + badge
 * classe), non solo quelle con setup interattivo. Le mosse description-only
 * vengono renderate senza `setupSlot` (read-only); quelle con setup
 * espongono `<MoveSetupPanel>` SSOT.
 *
 * **Stato locale isolato**: a differenza di `useMoveSetupState` (limitato
 * alle mosse intra-class), questo componente mantiene il setup delle
 * cross-class in un `useState` dedicato. I `MoveRef` cross-class compilati
 * vengono emessi via `onCrossClassRefsChange` al parent, che li accumula
 * in `draft.moves` (consumati da `characterCreation.finalizeCharacter`
 * via `applyStartingGrantedChoice` SSOT — la firma estesa con
 * `allDraftMoves` legge il setup dal MoveRef draft, senza creare stub).
 * Le mosse description-only NON contribuiscono a `refs` (vengono
 * materializzate come stub `{ id, selectedChoices: [] }` da
 * `applyStartingGrantedChoice` fallback).
 *
 * **Niente render** se nessuna mossa starting selezionata ha
 * `grantsMoveChoice from-choice-mapping` con choice setupped.
 */
export interface CrossClassGrantedSectionProps {
  /**
   * Le `MoveRef` correnti del PG (intra-class). Usate per derivare le
   * mosse concesse cross-class (anchor + choice → granted-mapping).
   */
  intraClassMoves: readonly MoveRef[];
  archetypeId: string | undefined;
  /**
   * Emette i `MoveRef` cross-class compilati (1 per ogni mossa granted con
   * setup). Il parent accumula in `draft.moves`.
   */
  onCrossClassRefsChange: (refs: readonly MoveRef[]) => void;
  /**
   * Set di id mossa cross-class compilate completamente (per spunta verde
   * nella `MoveCard`). Opzionale; il parent può sovrascrivere via override.
   */
  onSetupCompleteChange?: (completeIds: ReadonlySet<string>) => void;
}

interface GrantedDerivation {
  anchorMove: Move;
  anchorChoice: string;
  grantedMove: Move;
}

export function CrossClassGrantedSection({
  intraClassMoves,
  archetypeId,
  onCrossClassRefsChange,
  onSetupCompleteChange,
}: CrossClassGrantedSectionProps) {
  // Stato locale per i setup compilati delle cross-class. Map moveId → setup.
  const [setupsById, setSetupsById] = useState<Record<string, Omit<MoveRef, 'id'>>>({});

  // Compute derivazioni anchor → granted dalle mosse intra-class del PG.
  const derivations = useMemo<readonly GrantedDerivation[]>(
    () => deriveCrossClassGranted(intraClassMoves),
    [intraClassMoves],
  );

  // **FI-86**: mostriamo TUTTE le derivazioni cross-class, incluse le
  // description-only (es. Vincolo della Catena). Le card description-only
  // non hanno setupSlot e non contribuiscono a `refs` (vengono
  // materializzate come stub da `applyStartingGrantedChoice` fallback).
  // Sottoinsieme con setup interattivo (per emit refs + completion check).
  const derivationsWithSetup = useMemo<readonly GrantedDerivation[]>(
    () => derivations.filter((d) => hasMoveSetup(d.grantedMove)),
    [derivations],
  );

  // Cleanup: rimuovi setup di granted che non sono più derivate (es. l'utente
  // ha cambiato choice della anchor).
  useEffect(() => {
    const activeIds = new Set(derivationsWithSetup.map((d) => d.grantedMove.id));
    setSetupsById((prev) => {
      const next: Record<string, Omit<MoveRef, 'id'>> = {};
      let changed = false;
      for (const [id, setup] of Object.entries(prev)) {
        if (activeIds.has(id)) next[id] = setup;
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [derivationsWithSetup]);

  // Emetti i MoveRef compilati al parent solo se sono effettivamente
  // cambiati (deep compare via JSON). Evita un loop infinito: un emit
  // ridondante triggererebbe re-render del parent → re-render qui →
  // emit di nuovo. Solo le derivazioni con setup contribuiscono ai refs;
  // le description-only vengono materializzate come stub da
  // `applyStartingGrantedChoice` fallback (vedi note FI-86 sopra).
  const refs = useMemo<readonly MoveRef[]>(
    () =>
      derivationsWithSetup.map((d) =>
        buildGrantedRef(d.grantedMove.id, setupsById[d.grantedMove.id]),
      ),
    [derivationsWithSetup, setupsById],
  );
  const refsKey = useMemo(() => JSON.stringify(refs), [refs]);
  useEffect(() => {
    onCrossClassRefsChange(refs);
    // refsKey è la deep-compare key: emit solo quando la struttura cambia.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refsKey]);

  // Emetti l'insieme degli id completi (per spunta verde nella MoveCard).
  // Stesso pattern di dedup via JSON key per evitare re-render loop.
  const completeIdsKey = useMemo(() => {
    const ids: string[] = [];
    for (const d of derivationsWithSetup) {
      const ref = buildGrantedRef(d.grantedMove.id, setupsById[d.grantedMove.id]);
      if (isMoveSetupComplete(d.grantedMove, ref, archetypeId)) ids.push(d.grantedMove.id);
    }
    return ids.sort().join('|');
  }, [derivationsWithSetup, setupsById, archetypeId]);
  useEffect(() => {
    if (!onSetupCompleteChange) return;
    const complete = new Set<string>(completeIdsKey === '' ? [] : completeIdsKey.split('|'));
    onSetupCompleteChange(complete);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completeIdsKey]);

  const ownedMoveIds = useMemo(() => intraClassMoves.map((m) => m.id), [intraClassMoves]);

  const handleChange = useCallback((grantedId: string, next: Omit<MoveRef, 'id'>): void => {
    setSetupsById((prev) => ({ ...prev, [grantedId]: next }));
  }, []);

  if (derivations.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      <InkBlock variant="full">Mosse concesse</InkBlock>
      <div className="flex flex-col gap-[0.6rem]">
        {derivations.map((d) => (
          <GrantedMoveCard
            key={d.grantedMove.id}
            anchorMove={d.anchorMove}
            grantedMove={d.grantedMove}
            setup={setupsById[d.grantedMove.id]}
            archetypeId={archetypeId}
            ownedMoveIds={ownedMoveIds}
            onChange={(next) => handleChange(d.grantedMove.id, next)}
          />
        ))}
      </div>
    </div>
  );
}

interface GrantedMoveCardProps {
  anchorMove: Move;
  grantedMove: Move;
  setup: Omit<MoveRef, 'id'> | undefined;
  archetypeId: string | undefined;
  ownedMoveIds: readonly string[];
  onChange: (next: Omit<MoveRef, 'id'>) => void;
}

const noop = (): void => {
  /* MoveCard è espansa fissa per granted, no toggle */
};

function GrantedMoveCard({
  anchorMove,
  grantedMove,
  setup,
  archetypeId,
  ownedMoveIds,
  onChange,
}: GrantedMoveCardProps) {
  const moveRef = buildGrantedRef(grantedMove.id, setup);
  const grantedClassId = grantedMove.classId as ClassId | undefined;
  const grantedClassName = grantedClassId ? (dwrClassesById[grantedClassId]?.name ?? '') : '';
  const trailing = grantedClassName ? <ClassBadge name={grantedClassName} /> : undefined;
  // **FI-86**: solo le mosse con setup interattivo espongono il
  // `<MoveSetupPanel>`. Le description-only renderizzano la MoveCard
  // espansa senza setupSlot, senza badge `DA COMPILARE` né spunta verde.
  const moveHasSetup = hasMoveSetup(grantedMove);
  const setupSlot = moveHasSetup ? (
    <GrantedSetupSlot
      grantedMove={grantedMove}
      moveRef={moveRef}
      archetypeId={archetypeId}
      ownedMoveIds={ownedMoveIds}
      onChange={onChange}
    />
  ) : null;
  return (
    <MoveCard
      move={grantedMove}
      fallbackId={grantedMove.id}
      expanded={true}
      onToggle={noop}
      {...(moveHasSetup && {
        setupComplete: isMoveSetupComplete(grantedMove, moveRef, archetypeId),
      })}
      meta={
        <span className="text-ink-soft font-serif text-[0.85rem] italic">
          Concessa da «{anchorMove.name}».
        </span>
      }
      {...(trailing ? { trailing } : {})}
      {...(setupSlot ? { setupSlot } : {})}
    />
  );
}

function ClassBadge({ name }: { name: string }) {
  return (
    <span className="bg-ink-soft text-paper-bright font-display inline-block px-[0.45em] py-[0.1em] text-[0.6rem] tracking-[0.1em] uppercase">
      {name}
    </span>
  );
}

interface GrantedSetupSlotProps {
  grantedMove: Move;
  moveRef: MoveRef;
  archetypeId: string | undefined;
  ownedMoveIds: readonly string[];
  onChange: (next: Omit<MoveRef, 'id'>) => void;
}

function GrantedSetupSlot({
  grantedMove,
  moveRef,
  archetypeId,
  ownedMoveIds,
  onChange,
}: GrantedSetupSlotProps) {
  return (
    <MoveSetupPanel
      move={grantedMove}
      selectedByGroup={buildSelectedByGroup(grantedMove, moveRef)}
      narrativeValues={moveRef.narrativeFieldValues ?? {}}
      recruitOutcome={moveRef.recruitOutcome}
      archetypeId={archetypeId}
      ownedMoveIds={ownedMoveIds}
      onChoiceGroupChange={(moveId, groupId, selectedIds) => {
        const updated = updateMoveChoiceGroup(moveRef, moveId, groupId, selectedIds);
        onChange(extractSetup(updated));
      }}
      onNarrativeFieldChange={(moveId, fieldId, value) => {
        const updated = updateMoveNarrativeField(moveRef, moveId, fieldId, value);
        onChange(extractSetup(updated));
      }}
    />
  );
}

function buildGrantedRef(id: string, setup: Omit<MoveRef, 'id'> | undefined): MoveRef {
  return {
    id,
    selectedChoices: setup?.selectedChoices ?? [],
    ...(setup?.selectedChoiceGroups && { selectedChoiceGroups: setup.selectedChoiceGroups }),
    ...(setup?.narrativeFieldValues && { narrativeFieldValues: setup.narrativeFieldValues }),
  };
}

/**
 * Deriva le mosse cross-class granted da `from-choice-mapping` per ogni
 * mossa intra-class del PG che ha `grantsMoveChoice from-choice-mapping`
 * + `selectedChoices[0]` settato + mapping presente per la choice.
 */
function deriveCrossClassGranted(
  intraClassMoves: readonly MoveRef[],
): readonly GrantedDerivation[] {
  const out: GrantedDerivation[] = [];
  const seenGrantedIds = new Set<string>();
  for (const ref of intraClassMoves) {
    const anchorMove = findMoveByIdGlobal(ref.id);
    if (!anchorMove) continue;
    const grant = anchorMove.grantsMoveChoice;
    if (grant?.kind !== 'from-choice-mapping') continue;
    const choice = ref.selectedChoices[0];
    if (choice === undefined) continue;
    const grantedId = grant.mapping[choice];
    if (grantedId === undefined || seenGrantedIds.has(grantedId)) continue;
    const grantedMove = findMoveByIdGlobal(grantedId);
    if (!grantedMove) continue;
    seenGrantedIds.add(grantedId);
    out.push({ anchorMove, anchorChoice: choice, grantedMove });
  }
  return out;
}

function buildSelectedByGroup(move: Move, moveRef: MoveRef): Record<string, string[]> {
  const result: Record<string, string[]> = {};
  if (move.choices) result.main = [...moveRef.selectedChoices];
  if (moveRef.selectedChoiceGroups) {
    for (const [groupId, ids] of Object.entries(moveRef.selectedChoiceGroups)) {
      result[groupId] = [...ids];
    }
  }
  return result;
}

function extractSetup(moveRef: MoveRef): Omit<MoveRef, 'id'> {
  const setup: Omit<MoveRef, 'id'> = { selectedChoices: moveRef.selectedChoices };
  if (moveRef.selectedChoiceGroups) setup.selectedChoiceGroups = moveRef.selectedChoiceGroups;
  if (moveRef.narrativeFieldValues) setup.narrativeFieldValues = moveRef.narrativeFieldValues;
  return setup;
}
